import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Popover,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { X as CloseIcon } from "lucide-react";
import { ProfileIcon } from "../../../assets/icon";
import { useAppStore } from "../../../stores/appStore";
import {
  ResponseContextField,
  ResponseContextMenuResponse,
  ResponseContextOption,
} from "../../../types/realtimeSpeech";
import {
  ACCENT_PURPLE,
  closeButtonStyles,
  emptyStateTextStyles,
  fieldLabelStyles,
  fieldsContainerStyles,
  formControlStyles,
  getOptionIconBoxStyles,
  getSelectMenuPaperStyles,
  getSelectStyles,
  getSelectValueTextStyles,
  headerRowStyles,
  loadingContainerStyles,
  menuItemContentRowStyles,
  menuItemDescriptionStyles,
  menuItemStyles,
  menuItemTextStyles,
  optionAvatarSx,
  popoverSx,
  savingRowStyles,
  savingTextStyles,
  selectChevronStyle,
  selectValueRowStyles,
} from "./styles";
import { AuthenticatedAvatar } from "../../authenticated-media/authenticated-avatar";

interface ResponseContextMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const DEFAULT_OPTION_ID = "default";

const getFieldLabel = (field: ResponseContextField): string =>
  field.key === "engagement_level" ? "Adoption Stage" : field.label;

const getAvatar = (opt: ResponseContextOption): string | null =>
  opt.avatar || null;

const OptionIcon: React.FC<{ option: ResponseContextOption | null }> = ({
  option,
}) => {
  const theme = useTheme();
  const avatar = option ? getAvatar(option) : null;
  if (avatar) return <AuthenticatedAvatar src={avatar} sx={optionAvatarSx} />;
  return (
    <Box sx={getOptionIconBoxStyles(theme)}>
      <ProfileIcon size={16} />
    </Box>
  );
};

export const ResponseContextMenu: React.FC<ResponseContextMenuProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const {
    responseContext,
    setResponseContext,
    responseContextMenu,
    fetchResponseContextMenu,
  } = useAppStore();

  const [menuData, setMenuData] = useState<ResponseContextMenuResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fetchedRef = useRef(false);

  const [localPersonaId, setLocalPersonaId] = useState<string>(
    responseContext.persona_id ?? DEFAULT_OPTION_ID,
  );
  const [localEngagementId, setLocalEngagementId] = useState<string>(
    responseContext.engagement_level_id ?? DEFAULT_OPTION_ID,
  );

  useEffect(() => {
    if (open) {
      setLocalPersonaId(responseContext.persona_id ?? DEFAULT_OPTION_ID);
      setLocalEngagementId(
        responseContext.engagement_level_id ?? DEFAULT_OPTION_ID,
      );
    }
  }, [open, responseContext.persona_id, responseContext.engagement_level_id]);

  useEffect(() => {
    if (!open) return;
    if (responseContextMenu) {
      setMenuData(responseContextMenu);
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setIsLoading(true);
    fetchResponseContextMenu()
      .then((data) => setMenuData(data))
      .catch((e) => console.error("Failed to load response context menu:", e))
      .finally(() => setIsLoading(false));
  }, [open, responseContextMenu, fetchResponseContextMenu]);

  const handleChange = useCallback(
    async (key: "persona" | "engagement_level", id: string) => {
      const resolvedId = id === DEFAULT_OPTION_ID ? null : id;

      if (key === "persona") {
        setLocalPersonaId(id);
        setLocalEngagementId(DEFAULT_OPTION_ID);
      } else {
        setLocalEngagementId(id);
      }

      setIsSaving(true);
      try {
        await setResponseContext(
          key === "persona"
            ? { persona_id: resolvedId }
            : { engagement_level_id: resolvedId },
        );
      } catch (e) {
        console.error("Failed to save response context:", e);
        if (key === "persona")
          setLocalPersonaId(responseContext.persona_id ?? DEFAULT_OPTION_ID);
        else
          setLocalEngagementId(
            responseContext.engagement_level_id ?? DEFAULT_OPTION_ID,
          );
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [responseContext.persona_id, responseContext.engagement_level_id],
  );

  const personaField = menuData?.fields.find((f) => f.key === "persona");
  const engagementField = menuData?.fields.find(
    (f) => f.key === "engagement_level",
  );

  const renderSelect = (
    field: ResponseContextField,
    value: string,
    onChange: (id: string) => void,
  ) => {
    const isPersona = field.key === "persona";
    const selectedOpt =
      field.options.find((o) => o.id === value) ??
      field.options.find((o) => o.is_default) ??
      null;

    const isDefault: boolean =
      !selectedOpt || selectedOpt.is_default === true;

    return (
      <Box key={field.key}>
        <Typography sx={fieldLabelStyles}>{getFieldLabel(field)}</Typography>
        <FormControl fullWidth disabled={isSaving} sx={formControlStyles}>
          <Select
            value={value}
            displayEmpty
            IconComponent={() => null}
            renderValue={() => (
              <Box sx={selectValueRowStyles}>
                {isPersona && (
                  <OptionIcon option={isDefault ? null : selectedOpt} />
                )}
                <Typography sx={getSelectValueTextStyles(theme, isDefault)}>
                  {selectedOpt?.text ?? getFieldLabel(field)}
                </Typography>
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={selectChevronStyle}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </Box>
            )}
            sx={getSelectStyles(theme)}
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              PaperProps: {
                sx: getSelectMenuPaperStyles(theme),
              },
            }}
            onChange={(e) => onChange(e.target.value as string)}
          >
            {field.options.map((option) => (
              <MenuItem key={option.id} value={option.id} sx={menuItemStyles}>
                <Box sx={menuItemContentRowStyles}>
                  {isPersona && (
                    <OptionIcon option={option.is_default ? null : option} />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={menuItemTextStyles}>
                      {option.text}
                    </Typography>
                    {option.description && !option.is_default && (
                      <Typography sx={menuItemDescriptionStyles}>
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      sx={popoverSx}
    >
      <Box sx={headerRowStyles}>
        <Box component="button" onClick={onClose} sx={closeButtonStyles}>
          <CloseIcon size={18} />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={loadingContainerStyles}>
          <CircularProgress size={24} sx={{ color: ACCENT_PURPLE }} />
        </Box>
      ) : (
        <Box sx={fieldsContainerStyles}>
          {personaField &&
            renderSelect(personaField, localPersonaId, (id) =>
              handleChange("persona", id),
            )}
          {engagementField &&
            renderSelect(engagementField, localEngagementId, (id) =>
              handleChange("engagement_level", id),
            )}
          {!menuData && !isLoading && (
            <Typography sx={emptyStateTextStyles}>
              No options available
            </Typography>
          )}
        </Box>
      )}

      {isSaving && (
        <Box sx={savingRowStyles}>
          <CircularProgress size={12} sx={{ color: ACCENT_PURPLE }} />
          <Typography sx={savingTextStyles}>Saving...</Typography>
        </Box>
      )}
    </Popover>
  );
};
